import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Pin, Paperclip, Clock, Activity } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";

interface Comment {
  id: string;
  content: string;
  user_id: string;
  org_id: string;
  lead_id?: string;
  job_id?: string;
  attachments: any;
  pinned: boolean;
  is_system_activity: boolean;
  activity_type?: string;
  activity_metadata?: any;
  created_at: string;
  updated_at: string;
  profiles?: {
    full_name: string;
    email: string;
  };
}

interface CollaborationFeedProps {
  leadId?: string;
  jobId?: string;
  orgId: string;
}

export function CollaborationFeed({ leadId, jobId, orgId }: CollaborationFeedProps) {
  const { user } = useAuth();
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadComments();
    subscribeToChanges();
  }, [leadId, jobId]);

  const loadComments = async () => {
    try {
      const query = supabase
        .from("comments")
        .select(`
          *
        `)
        .eq("org_id", orgId)
        .order("created_at", { ascending: false });

      if (leadId) {
        query.eq("lead_id", leadId);
      } else if (jobId) {
        query.eq("job_id", jobId);
      }

      const { data: comments, error } = await query;

      if (error) throw error;

      // Fetch user profiles for all comments
      if (comments && comments.length > 0) {
        const userIds = [...new Set(comments.map((c: any) => c.user_id))];
        const { data: profiles } = await supabase
          .from("profiles")
          .select("id, full_name, email")
          .in("id", userIds);

        const profileMap = new Map(profiles?.map((p) => [p.id, p]));
        const commentsWithProfiles = comments.map((comment: any) => ({
          ...comment,
          profiles: profileMap.get(comment.user_id),
        }));

        setComments(commentsWithProfiles);
      } else {
        setComments([]);
      }
    } catch (error: any) {
      console.error("Error loading comments:", error);
      toast.error("Failed to load comments");
    } finally {
      setLoading(false);
    }
  };

  const subscribeToChanges = () => {
    const channel = supabase
      .channel("comments-changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "comments",
          filter: leadId ? `lead_id=eq.${leadId}` : `job_id=eq.${jobId}`,
        },
        () => {
          loadComments();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  const togglePin = async (commentId: string, currentPinned: boolean) => {
    try {
      const { error } = await supabase
        .from("comments")
        .update({ pinned: !currentPinned })
        .eq("id", commentId);

      if (error) throw error;
      loadComments();
      toast.success(currentPinned ? "Comment unpinned" : "Comment pinned");
    } catch (error: any) {
      console.error("Error toggling pin:", error);
      toast.error("Failed to update comment");
    }
  };

  const getInitials = (name?: string) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <ScrollArea className="h-[500px] pr-4">
      <div className="space-y-4">
        {comments.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Activity className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>No activity yet. Start the conversation!</p>
          </div>
        ) : (
          comments.map((comment) => (
            <div
              key={comment.id}
              className={`rounded-lg border p-4 ${
                comment.is_system_activity
                  ? "bg-muted/30 border-muted"
                  : "bg-card border-border"
              }`}
            >
              <div className="flex items-start gap-3">
                {!comment.is_system_activity && (
                  <Avatar className="h-9 w-9">
                    <AvatarImage src={`https://avatar.vercel.sh/${comment.user_id}`} />
                    <AvatarFallback className="bg-primary/10 text-primary">
                      {getInitials(comment.profiles?.full_name)}
                    </AvatarFallback>
                  </Avatar>
                )}
                {comment.is_system_activity && (
                  <div className="h-9 w-9 rounded-full bg-muted flex items-center justify-center">
                    <Activity className="h-4 w-4 text-muted-foreground" />
                  </div>
                )}

                <div className="flex-1 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">
                        {comment.is_system_activity
                          ? "System"
                          : comment.profiles?.full_name || "Unknown User"}
                      </span>
                      {comment.pinned && (
                        <Badge variant="secondary" className="h-5">
                          <Pin className="h-3 w-3 mr-1" />
                          Pinned
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatDistanceToNow(new Date(comment.created_at), {
                          addSuffix: true,
                        })}
                      </span>
                      {!comment.is_system_activity && comment.user_id === user?.id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => togglePin(comment.id, comment.pinned)}
                          className="h-6 px-2"
                        >
                          <Pin className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  </div>

                  <p className="text-sm whitespace-pre-wrap">{comment.content}</p>

                  {comment.attachments && Array.isArray(comment.attachments) && comment.attachments.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {comment.attachments.map((attachment: string, idx: number) => (
                        <a
                          key={idx}
                          href={attachment}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-xs text-primary hover:underline"
                        >
                          <Paperclip className="h-3 w-3" />
                          Attachment {idx + 1}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </ScrollArea>
  );
}
